import { useEffect, useState } from "react";
import { Settings2, Plus, X, Pencil, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { notifyOptionsChanged } from "@/lib/optionsBus";

/**
 * ManageOptionsDialog — Add/Rename/Delete options for a type, with smooth optimistic UI.
 * Broadcasts via notifyOptionsChanged so all consumers refresh live.
 */
export default function ManageOptionsDialog({ optionType, label, testId }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [newItem, setNewItem] = useState("");
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchItems = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/options/${optionType}`);
      setItems(data || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setLoading(false); }
  };

  useEffect(() => { if (open) fetchItems(); /* eslint-disable-next-line */ }, [open]);

  const add = async () => {
    const v = newItem.trim();
    if (!v) return;
    if (items.some((it) => it.toLowerCase() === v.toLowerCase())) {
      toast.error("Already exists");
      return;
    }
    // Optimistic update
    const prev = items;
    setItems((p) => [...p, v].sort((a, b) => a.localeCompare(b)));
    setNewItem("");
    try {
      await api.post(`/options/${optionType}`, { value: v });
      notifyOptionsChanged(optionType);
      toast.success("Option added");
    } catch (e) {
      setItems(prev);
      toast.error(formatApiError(e));
    }
  };

  const saveRename = async () => {
    const v = (editing?.draft || "").trim();
    const old = editing?.value;
    if (!v || v === old) { setEditing(null); return; }
    if (items.some((it) => it.toLowerCase() === v.toLowerCase() && it !== old)) {
      toast.error("Target value already exists");
      return;
    }
    const prev = items;
    setItems((p) => p.map((x) => (x === old ? v : x)).sort((a, b) => a.localeCompare(b)));
    setEditing(null);
    try {
      await api.put(`/options/${optionType}`, { value: old, new_value: v });
      notifyOptionsChanged(optionType);
      toast.success("Option renamed");
    } catch (e) {
      setItems(prev);
      toast.error(formatApiError(e));
    }
  };

  const remove = async (value) => {
    const prev = items;
    setItems((p) => p.filter((x) => x !== value));
    try {
      await api.delete(`/options/${optionType}`, { params: { value } });
      notifyOptionsChanged(optionType);
    } catch (e) {
      setItems(prev);
      toast.error(formatApiError(e));
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-7 px-2 text-[11px] gap-1 text-[#475569] hover:text-[#0055FF]"
          data-testid={testId}
        >
          <Settings2 className="w-3.5 h-3.5" /> Manage
        </Button>
      </DialogTrigger>
      <DialogContent data-testid={`${testId}-dialog`} className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight">Manage {label}</DialogTitle>
          <DialogDescription>Add, rename or remove options. Existing records that already used these values are unaffected.</DialogDescription>
        </DialogHeader>

        <div className="flex gap-2">
          <Input
            value={newItem}
            onChange={(e) => setNewItem(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
            placeholder={`Add new ${label.toLowerCase()}…`}
            data-testid={`${testId}-new-input`}
          />
          <Button onClick={add} className="bg-[#0055FF] hover:bg-[#0040C1] text-white gap-1" data-testid={`${testId}-add`}>
            <Plus className="w-4 h-4" /> Add
          </Button>
        </div>

        <div className="border border-[#E2E8F0] rounded-md max-h-72 overflow-auto divide-y">
          {loading ? (
            <div className="py-6 text-center text-sm text-[#64748B]">Loading…</div>
          ) : items.length === 0 ? (
            <div className="py-6 text-center text-sm text-[#64748B]">No options yet.</div>
          ) : items.map((it) => (
            <div key={it} className="flex items-center gap-2 px-3 py-2 transition-colors hover:bg-[#F8F9FA]">
              {editing?.value === it ? (
                <>
                  <Input
                    value={editing.draft}
                    onChange={(e) => setEditing({ ...editing, draft: e.target.value })}
                    className="h-8"
                    autoFocus
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); saveRename(); } }}
                    data-testid={`${testId}-edit-input-${it}`}
                  />
                  <Button size="sm" variant="ghost" onClick={saveRename} data-testid={`${testId}-edit-save-${it}`}>
                    <Check className="w-4 h-4 text-green-600" />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setEditing(null)}>
                    <X className="w-4 h-4" />
                  </Button>
                </>
              ) : (
                <>
                  <div className="flex-1 text-sm">{it}</div>
                  <Button size="sm" variant="ghost" onClick={() => setEditing({ value: it, draft: it })} data-testid={`${testId}-edit-${it}`}>
                    <Pencil className="w-3.5 h-3.5" />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => remove(it)} data-testid={`${testId}-delete-${it}`}>
                    <X className="w-4 h-4 text-[#EF4444]" />
                  </Button>
                </>
              )}
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
